import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WipeLink } from './Wipe';
import { CHAPTERS, EASE } from '../data/site';
import { FadeUp, Eyebrow } from './Reveal';

const ChapterIndex = () => {
  const [active, setActive] = useState(null);

  return (
    <section className="relative py-24 md:py-36 px-5 md:px-10 lg:px-24" data-testid="chapter-index">
      <Eyebrow color="#B50A00">The Book, Five Movements</Eyebrow>
      <FadeUp delay={0.05}>
        <h2 className="mt-6 text-4xl md:text-6xl font-light tracking-tighter leading-none max-w-3xl">
          Read it like a book.<br />Feel it like a system.
        </h2>
      </FadeUp>

      <div className="mt-16 md:mt-24 border-t border-ink/15" onMouseLeave={() => setActive(null)}>
        {CHAPTERS.map((c, i) => (
          <FadeUp key={c.slug} delay={i * 0.05}>
            <WipeLink
              to={`/chapter/${c.slug}`}
              data-testid={`chapter-index-${c.slug}`}
              onMouseEnter={() => setActive(i)}
              className="group relative grid grid-cols-12 items-center gap-4 border-b border-ink/15 py-8 md:py-10 transition-colors duration-500"
            >
              <motion.div
                className="absolute inset-0 -z-10 origin-bottom"
                style={{ background: c.color }}
                initial={false}
                animate={{ scaleY: active === i ? 1 : 0 }}
                transition={{ duration: 0.45, ease: EASE }}
              />
              <span
                className={`col-span-3 md:col-span-2 text-4xl md:text-6xl font-light tracking-tighter transition-colors duration-500 ${
                  active === i ? 'text-paper' : 'text-stroke text-ink/60'
                }`}
              >
                {c.n}
              </span>
              <span className="col-span-9 md:col-span-4">
                <span
                  className={`block text-2xl md:text-4xl font-light tracking-tight transition-colors duration-500 ${
                    active === i ? 'text-paper' : 'text-ink'
                  }`}
                >
                  {c.title}
                </span>
                <span
                  className={`mt-1 block text-[11px] md:text-xs uppercase tracking-[0.25em] transition-colors duration-500 ${
                    active === i ? 'text-paper/70' : 'text-support'
                  }`}
                >
                  {c.keys}
                </span>
              </span>
              <span
                className={`hidden md:block md:col-span-4 text-sm leading-relaxed transition-colors duration-500 ${
                  active === i ? 'text-paper/85' : 'text-support'
                }`}
              >
                {c.tag}
              </span>
              <span
                className={`hidden md:flex col-span-2 justify-end text-2xl transition-all duration-500 ${
                  active === i ? 'text-paper translate-x-0 opacity-100' : 'text-ink/30 -translate-x-2 opacity-60'
                }`}
              >
                →
              </span>
            </WipeLink>
          </FadeUp>
        ))}
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            key={active}
            initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 3 }}
            exit={{ opacity: 0, scale: 0.92, rotate: 6 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="pointer-events-none fixed right-10 top-1/2 z-40 hidden lg:block w-64 xl:w-80 -translate-y-1/2"
          >
            <img
              src={CHAPTERS[active].img}
              alt={CHAPTERS[active].title}
              className="w-full aspect-[4/5] object-cover shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ChapterIndex;
